import PowerSettingsNewRounded from '@mui/icons-material/PowerSettingsNewRounded';
import {
  Divider, FormControlLabel, MenuItem, Switch, Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import AvatarIndicator from '../../../../../Common/AvatorIndicator';
import RoleTag from '../../../../../Common/NotificationButton/Components/NotificationMenu/NotificationList/NotificationBox/Components/RoleTag';
import config from '../../../../../config';
import { clearHostedUiStorage } from '../../../../../dualChannel/hostedUi';
import useAmplifyContext from '../../../../Contexts/AmplifyContext';
import { useOrganisationData } from '../../../../Hooks/useOrganisation';
import { useCookieContext, useUserInfoData } from '../../../../Hooks/useUserInfo';
import useIsAppEnabled from '../../../../LiveContext/ApplicationSyncContext/useIsAppEnabled';
import {
  applyChannelRedirect, isNightlyChannelEnabled, isOnNightly, NIGHTLY_STORAGE_KEY,
} from '../../../../Contexts/SwContext/versionPolling';
import StyledMenu from './styles';

export default function AccountMenu() {
  const [accountAnchorEl, setAccountAnchorEl] = React.useState<null | HTMLElement>(null);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const userInfo = useUserInfoData();
  const organisation = useOrganisationData();
  const { removeToken } = useCookieContext();
  const { signOut, refreshToken } = useAmplifyContext();
  const isAppEnabled = useIsAppEnabled();
  const canReadDualCanalVersion = isAppEnabled && Boolean(window.utilsAPI);

  const dualCanalVersion = useQuery(['dual-canal-header-version'], () => {
    const { utilsAPI } = window;
    if (!utilsAPI) {
      return null;
    }

    return utilsAPI.getVersion();
  }, {
    enabled: canReadDualCanalVersion,
  });

  const logout = React.useCallback(() => {
    if (isAppEnabled && window.electronMessagingApi) {
      removeToken('manual');
      clearHostedUiStorage().then(() => { window.location.reload(); }).catch(() => undefined);
    } else {
      removeToken('manual');
      if (signOut) {
        signOut();
      }
    }
    setAccountAnchorEl(null);
  }, [removeToken, signOut, isAppEnabled]);

  const handleRefreshUserData = React.useCallback(() => {
    refreshToken();
    queryClient.invalidateQueries();
    setAccountAnchorEl(null);
  }, [refreshToken, queryClient]);

  const dualCanalVersionText = React.useMemo(() => {
    if (!canReadDualCanalVersion) {
      return null;
    }

    if (dualCanalVersion.data) {
      return dualCanalVersion.data;
    }

    return t('header.version-loading');
  }, [canReadDualCanalVersion, dualCanalVersion.data, t]);

  const nightlyChannelEnabled = React.useMemo(() => isNightlyChannelEnabled(), []);
  const onNightly = React.useMemo(() => isOnNightly(), []);

  const handleNightlyToggle = React.useCallback(() => {
    if (localStorage.getItem(NIGHTLY_STORAGE_KEY) !== null) {
      localStorage.removeItem(NIGHTLY_STORAGE_KEY);
    } else {
      localStorage.setItem(NIGHTLY_STORAGE_KEY, '1');
    }
    applyChannelRedirect();
    setAccountAnchorEl(null);
  }, []);

  const staffMenuItems = userInfo.isStaff ? [
    (
      <MenuItem key="staff-refresh" onClick={handleRefreshUserData} className="menuItem">
        <Typography variant="body1" className="menuText">{t('header.refresh')}</Typography>
      </MenuItem>
    ),
    ...(nightlyChannelEnabled ? [(
      <MenuItem key="staff-nightly" onClick={handleNightlyToggle} className="menuItem">
        <FormControlLabel
          control={<Switch checked={onNightly} size="small" />}
          label={<Typography variant="body1" className="menuText">{t('header.nightly-channel')}</Typography>}
          labelPlacement="start"
          sx={{ margin: 0, gap: 1 }}
        />
      </MenuItem>
    )] : []),
    <Divider key="staff-divider" variant="middle" className="divider" />,
  ] : [];

  return (
    <>
      <AvatarIndicator
        onClick={(event) => { setAccountAnchorEl((prev) => (prev ? null : event.currentTarget)); }}
        user={userInfo}
      />
      <StyledMenu
        anchorEl={accountAnchorEl}
        id="account-menu"
        open={!!accountAnchorEl}
        onClose={() => { setAccountAnchorEl(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div className="wrapper">
          <Typography className="displayName">{userInfo.displayName}</Typography>
          <Typography className="basicText">{userInfo.email}</Typography>
          <Typography className="basicText">{organisation.displayText}</Typography>
          {userInfo.role >= config.Roles.Admin && <RoleTag role={userInfo.role} />}
        </div>
        <Divider variant="middle" className="divider" />
        <div className="wrapper">
          <Typography className="sectionLabel">{t('header.versions')}</Typography>
          <Typography className="basicText">{t('header.live-version', { version: config.AppVersion })}</Typography>
          {dualCanalVersionText && (
            <Typography className="basicText">
              {t('header.dual-canal-version', { version: dualCanalVersionText })}
            </Typography>
          )}
        </div>
        <Divider variant="middle" className="divider" />
        {staffMenuItems}
        <MenuItem onClick={logout} className="menuItem2">
          <PowerSettingsNewRounded className="powerSettings" />
          <Typography variant="body1" className="menuText">{t('header.logout')}</Typography>
        </MenuItem>
      </StyledMenu>
    </>
  );
}
